
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

export interface ExperienceData {
  currentLevel: number;
  currentRank: string;
  totalXp: number;
  nextLevelXp: number;
  progressPercentage: number;
}

const BASE_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2100, 2800, 3600, 4500, 5500, 6600, 7800, 9100, 10500, 12000, 13600, 15300, 17100, 19000];

// For level N > 20: threshold = 19000 + sum_{k=21}^{N} (2000 + (k-20)*150)
// Simplified: threshold(N) = 19000 + (N-20)*2000 + 150*(N-20)*(N-19)/2
const getThreshold = (level: number): number => {
  if (level <= 0) return 0;
  if (level <= 20) return BASE_THRESHOLDS[level - 1];
  const n = level - 20;
  return 19000 + n * 2000 + 150 * n * (n + 1) / 2;
};

const calculateLevel = (xp: number): number => {
  if (xp < 19000) {
    for (let i = 0; i < BASE_THRESHOLDS.length; i++) {
      if (xp < BASE_THRESHOLDS[i]) return i;
    }
    return 20;
  }
  // Binary search for levels 20-999
  let lo = 20, hi = 999;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (getThreshold(mid) <= xp) lo = mid;
    else hi = mid - 1;
  }
  return lo;
};

const calculateNextLevelXp = (level: number): number => {
  if (level >= 999) return getThreshold(999);
  return getThreshold(level + 1);
};

const getRankFromLevel = (level: number): string => {
  if (level <= 2) return 'Neophyte';
  if (level <= 4) return 'Acolyte';
  if (level <= 6) return 'Seeker';
  if (level <= 8) return 'Initiate';
  if (level <= 10) return 'Adept';
  if (level <= 12) return 'Keeper';
  if (level <= 14) return 'Architect';
  if (level <= 16) return 'Ascendant';
  if (level <= 18) return 'Harbinger';
  return 'Master';
};

export const useExperience = () => {
  const { user } = useAuth();
  
  const { data: experience, isLoading, error } = useQuery({
    queryKey: ['user-experience', user?.id],
    queryFn: async (): Promise<ExperienceData | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_experience')
        .select('total_xp')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user experience:', error);
        return null;
      }

      const totalXp = data?.total_xp || 0;
      const currentLevel = calculateLevel(totalXp);
      const currentRank = getRankFromLevel(currentLevel);
      const nextLevelXp = calculateNextLevelXp(currentLevel);
      const currentLevelXp = getThreshold(currentLevel);
      const progressPercentage = currentLevel >= 999 ? 100 : ((totalXp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100;

      return {
        currentLevel,
        currentRank,
        totalXp,
        nextLevelXp,
        progressPercentage: Math.max(0, Math.min(100, progressPercentage))
      };
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes - reduced refetching
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });

  return {
    experience,
    isLoading,
    error
  };
};
